import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import { ArrowRightIcon, Link2OffIcon, Loader2Icon } from "lucide-react";
import { useQuery } from "@util/useQuery";
import ErrorStateCard from "@component/error-state-card";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getCompanyInfo } from "@service/user";
import { Company } from "@type/company";
import BasicLayout from "@container/layout/basic";
import { setCookie } from "@util/cookie";
import { activateWorkspaceCompany } from "@service/employer";
import { Checkbox } from "@component/checkbox";

interface PayloadToken {
    company_id: string;
    access_token: string;
    workspace_email: string;
}

export default function ApproveActivationWorkspace() {
    const queryParams = useQuery()
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [companyInfo, setCompanyInfo] = useState<Company | null>(null);
    const [countDayLinkExpired, setCountDayLinkExpired] = useState<number>(0);
    const [isInvalidToken, setIsInvalidToken] = useState(false);
    const [isAlreadyActivated, setIsAlreadyActivated] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const token = queryParams.get("token") || "";

    let payloadToken: PayloadToken | null = null;
    try {
        payloadToken = token ? JSON.parse(atob(token)) : null;
    } catch (_) {
        payloadToken = null;
    }

    useEffect(() => {
        if (!token || !payloadToken || !payloadToken.company_id) {
            setIsInvalidToken(true);
            setIsLoading(false);
            return;
        }

        if (payloadToken && payloadToken.access_token && payloadToken.company_id) {
            getCompanyInfo(payloadToken.company_id).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);
                    setCookie('tempAuthToken', payloadToken!.access_token);

                    if (resp.data.data.workspace_link_expired_date) {
                        // calcuklate days left to link expired from date now
                        const expiredDate = new Date(resp.data.data.workspace_link_expired_date || "");
                        const currentDate = new Date();
                        const timeDiff = expiredDate.getTime() - currentDate.getTime();
                        const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
                        setCountDayLinkExpired(daysLeft);
                    }
                }
            }).finally(() => {
                setIsLoading(false);
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleActivateWorkspace = () => {
        if (!companyInfo) return;

        setIsLoading(true);
        activateWorkspaceCompany(companyInfo?.id || "").then((resp) => {
            if (resp.status === 200) {
                navigate(`/workspace/setup?token=${queryParams.get("token")}`); // redirect to setup password page
            }
        }).catch((err) => {
            if (err?.response?.status === 400 && err?.response?.data?.data?.is_already_activated) {
                setIsAlreadyActivated(true);
            }
        }).finally(() => {
            setIsLoading(false);
        })
    }

    if (!isLoading && isAlreadyActivated) {
        return (
            <BasicLayout showFooter={false}>
                <div className="container mx-auto w-xl h-full justify-center align-middle items-center flex">
                    <div className="m-4 sm:m-0">
                        <ErrorStateCard
                            icon={Link2OffIcon}
                            title="Workspace has already been activated."
                            description={(
                                <>
                                    This workspace has already been activated. <br />
                                    Please contact <u>support@ourteam.ai</u> for assistance.
                                </>
                            )}
                            iconBgColor="bg-amber-100"
                            iconTextColor="text-amber-700"
                        />
                    </div>
                </div>
            </BasicLayout>
        )
    }

    if (!isLoading && isInvalidToken) {
        return (
            <BasicLayout showFooter={false}>
                <div className="container mx-auto w-xl h-full justify-center align-middle items-center flex">
                    <div className="m-4 sm:m-0">
                        <ErrorStateCard
                            icon={Link2OffIcon}
                            title="Invalid Link"
                            description={(
                                <>
                                    This activation link is not valid. <br /> It may have expired or been deactivated. <br />
                                    Please contact <u>support@ourteam.ai</u> for assistance.
                                </>
                            )}
                            iconBgColor="bg-amber-100"
                            iconTextColor="text-amber-700"
                        />
                    </div>
                </div>
            </BasicLayout>
        )
    }

    if (!isLoading && countDayLinkExpired <= 0) {
        return (
            <BasicLayout showFooter={false}>
                <div className="container mx-auto w-xl h-full justify-center align-middle items-center flex">
                    <div className="m-4 sm:m-0">
                        <ErrorStateCard
                            icon={Link2OffIcon}
                            title="Link Expired"
                            description={(
                                <>
                                    This activation link has expired and can no longer be used. <br />
                                    Please contact <u>support@ourteam.ai</u> for assistance.
                                </>
                            )}
                        />
                    </div>
                </div>
            </BasicLayout>
        )
    }

    return (
        <BasicLayout showFooter={false}>
            <div className="flex items-center justify-center min-h-full">
                <Card className="w-full max-w-4xl shadow-md">
                    <CardContent className="p-8 space-y-6">
                        {/* Main Content */}
                        <div className="flex flex-col items-center text-center space-y-6">
                            {/* Static Header and Description */}
                            <div className="text-center space-y-3">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Activate Your Workspace
                                </h2>
                                <div className="space-y-1">
                                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                        You're about to activate your company workspace as the Workspace
                                        Owner.
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                                        Your link will expire on{" "}
                                        <span className="font-bold text-gray-900 dark:text-white">
                                            {companyInfo?.company_name}
                                        </span>{" "}
                                        (in{" "}
                                        <span className="font-bold text-gray-900 dark:text-white">
                                            {countDayLinkExpired} days
                                        </span>
                                        ).
                                    </p>
                                </div>
                                <div className="w-full">
                                    {/* Workspace Info Card */}
                                    <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50/30 dark:from-blue-950/20 dark:via-gray-900 dark:to-blue-950/10 dark:border-blue-900/50">
                                        {/* Decorative background elements */}
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/40 dark:bg-blue-900/20 rounded-full blur-3xl" />
                                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-200/30 dark:bg-blue-800/20 rounded-full blur-2xl" />
                                        <div className="relative p-6">
                                            <div className="flex flex-col items-center space-y-4 text-center">
                                                {/* Company Logo */}
                                                <div className="relative flex-shrink-0 w-16 h-16 rounded-full overflow-hidden flex justify-center items-center bg-white ring-2 ring-blue-200 dark:ring-blue-800 ring-offset-2 dark:ring-offset-gray-900">
                                                    <img
                                                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${companyInfo?.preferences?.company_logo}`}
                                                        alt={`${companyInfo?.company_name} logo`}
                                                        onError={(e) => {
                                                            e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/company_placeholder.png`;
                                                        }}
                                                        className="relative w-16 object-cover "
                                                    />
                                                </div>
                                                {/* Workspace Details */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                                        {companyInfo?.company_name}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        {payloadToken?.workspace_email || ""}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Terms of Use Checkbox */}
                            <div className="flex items-start gap-3 w-full">
                                <Checkbox
                                    id="terms"
                                    checked={termsAccepted}
                                    onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                                    className="mt-0.5"
                                />
                                <label
                                    htmlFor="terms"
                                    className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer leading-relaxed"
                                >
                                    By proceeding, you agree to the{" "}
                                    <a
                                        href="https://ourteam.ai/terms-of-service"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline text-blue-600 dark:text-blue-400 hover:text-black dark:hover:text-white"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Terms of Service
                                    </a>{" "}
                                    at ourteam.
                                </label>
                            </div>

                            {/* CTA Button */}
                            <Button
                                onClick={handleActivateWorkspace}
                                disabled={isLoading || !termsAccepted}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                                style={{ paddingTop: "16px", paddingBottom: "16px" }}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                        Activating workspace...
                                    </>
                                ) : (
                                    <>
                                        Activate Workspace
                                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </BasicLayout >
    );
}
